import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Skeleton,
    Switch,
    TextField,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import StyledCardTitleBar from '../../common/StyledCardTitleBar';

export type AdminEditableFieldType = 'text' | 'email' | 'password' | 'multiline' | 'switch';

export interface AdminEditableField {
    /** Dotted path of the field in the update payload, e.g. "cognito.username" */
    key: string;
    label: string;
    type?: AdminEditableFieldType;
    required?: boolean;
    helperText?: string;
    /** Grid width on sm+ screens (12 = full row). Defaults to 12 */
    gridWidth?: 6 | 12;
}

export type AdminSectionValues = Record<string, string | boolean | undefined>;

type Props = {
    title: string;
    fields: AdminEditableField[];
    /** Current (server) values keyed by the field's dotted path */
    values: AdminSectionValues;
    isLoading?: boolean;
    /**
     * Called with only the fields that were touched and changed.
     * Return true on a successful save so the card can clear its dirty state.
     */
    onSave: (changed: AdminSectionValues) => Promise<boolean>;
};

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/**
 * A generic admin edit section. Renders the given fields, tracks which ones
 * were touched against the server values and submits only the changed fields.
 */
const AdminSubscriberEditSectionCard = (props: Props) => {
    const { title, fields, values, isLoading, onSave } = props;

    const [draft, setDraft] = useState<AdminSectionValues>(values);
    const [saving, setSaving] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});

    // Re-hydrate the draft whenever fresh server values arrive (initial load,
    // after a save or a refetch)
    const valuesFingerprint = JSON.stringify(values);

    useEffect(() => {
        setDraft(values);
    }, [valuesFingerprint]);

    const normalise = (field: AdminEditableField, value: string | boolean | undefined): string | boolean =>
        field.type === 'switch' ? value === true : value ?? '';

    const changedFields = useMemo(
        () => fields.filter(field => normalise(field, draft[field.key]) !== normalise(field, values[field.key])),
        [fields, draft, values],
    );

    const fieldError = (field: AdminEditableField): string | null => {
        const value = draft[field.key];

        if (field.type === 'switch') {
            return null;
        }

        const text = typeof value === 'string' ? value.trim() : '';

        if (field.required && text === '') {
            return `${field.label} is required`;
        }

        if (field.type === 'email' && text !== '' && !isValidEmail(text)) {
            return 'Enter a valid email address';
        }

        return null;
    };

    const hasErrors = fields.some(field => fieldError(field) !== null);
    const isDirty = changedFields.length > 0;

    const handleSave = async () => {
        if (!isDirty || hasErrors || saving) {
            return;
        }

        const changed: AdminSectionValues = {};

        changedFields.forEach(field => {
            changed[field.key] = normalise(field, draft[field.key]);
        });

        setSaving(true);

        await onSave(changed).finally(() => {
            setSaving(false);
        });
    };

    return (
        <Card variant="elevation" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <StyledCardTitleBar
                title={title}
                badge={isDirty ? <Chip label="Unsaved" size="small" color="warning" sx={{ fontWeight: 'bold' }} /> : undefined}
                isLoading={isLoading}
            />
            <CardContent sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    {fields.map(field => (
                        <Grid item xs={12} sm={field.gridWidth || 12} key={field.key}>
                            {isLoading ? (
                                <Skeleton variant="rounded" height={field.type === 'multiline' ? 120 : 40} />
                            ) : field.type === 'switch' ? (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={draft[field.key] === true}
                                            onChange={(_e, checked) => setDraft(prev => ({ ...prev, [field.key]: checked }))}
                                        />
                                    }
                                    label={field.label}
                                />
                            ) : (
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={field.label}
                                    type={field.type === 'password' && !showPassword[field.key] ? 'password' : 'text'}
                                    multiline={field.type === 'multiline'}
                                    minRows={field.type === 'multiline' ? 4 : undefined}
                                    required={field.required}
                                    value={typeof draft[field.key] === 'string' ? (draft[field.key] as string) : ''}
                                    onChange={e => setDraft(prev => ({ ...prev, [field.key]: e.target.value }))}
                                    error={fieldError(field) !== null}
                                    helperText={fieldError(field) || field.helperText}
                                    InputProps={
                                        field.type === 'password'
                                            ? {
                                                  endAdornment: (
                                                      <InputAdornment position="end">
                                                          <IconButton
                                                              aria-label="toggle password visibility"
                                                              size="small"
                                                              onClick={() =>
                                                                  setShowPassword(prev => ({
                                                                      ...prev,
                                                                      [field.key]: !prev[field.key],
                                                                  }))
                                                              }
                                                          >
                                                              {showPassword[field.key] ? <VisibilityOff /> : <Visibility />}
                                                          </IconButton>
                                                      </InputAdornment>
                                                  ),
                                              }
                                            : undefined
                                    }
                                />
                            )}
                        </Grid>
                    ))}
                </Grid>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', paddingX: 2, paddingBottom: 2 }}>
                <Button
                    size="small"
                    color="secondary"
                    startIcon={<RestartAltIcon />}
                    disabled={!isDirty || saving || isLoading}
                    onClick={() => setDraft(values)}
                >
                    Reset
                </Button>
                <LoadingButton
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    loading={saving}
                    disabled={!isDirty || hasErrors || isLoading}
                    onClick={handleSave}
                >
                    Save
                </LoadingButton>
            </CardActions>
        </Card>
    );
};

export default AdminSubscriberEditSectionCard;
